import { ButtonHTMLAttributes } from 'react';
import { IconType } from 'react-icons';
import { RightArrowIcon, StopIcon } from "../../utils/iconFactory"
import { classNames } from "../../utils/utils"
import Tooltip from './Tooltip';
import { PulseLoader } from 'react-spinners';
interface GenerateButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  stopState: boolean;
}

export function GenerateButton({ stopState, disabled, className, ...props }: GenerateButtonProps) {
  const buttonLabel = stopState
    ? "Generálás leállítása"
    : stopState
    ? "Új szöveg generálása"
    : "Szöveg generálása";

  return (
    <button className={classNames("px-4 py-3 rounded-[12px] w-full text-style-body2-bold",
      (stopState) ? 
        "flex justify-center bg-backgroundWhite text-primaryAction border border-primaryAction, secondary-button-drop-shadow secondary-button-hover-drop-shadow" 
        : disabled ? "disabled:bg-gray40 disabled:text-backgroundWhite"
        : "bg-primaryAction text-backgroundWhite primary-button-drop-shadow primary-button-hover-drop-shadow",
      className)}
      disabled={disabled}
      {...props}
    >
      {
        stopState && <StopIcon className="h-6 w-6 mr-2 fill-current" />
      }
      {buttonLabel}
    </button>
  )
}

interface ToEditorButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {}

export function ToEditorButton({className, disabled, ...props}: ToEditorButtonProps) {
  return (
    <button id='toEditor-button' className={classNames("absolute group top-3/4 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-[80px] w-[80px]",
      "p-[19.2px] rounded-full cursor-pointer",
      disabled ? "bg-gray40" : "bg-primaryAction to-edit-button-shadow hover:bg-primaryActionBtnHover to-edit-button-hover-shadow",
      className)}
      disabled={disabled}
      {...props}
    >
        <Tooltip message="Szöveg átvitele a szerkesztői panelba" widthPx={295} />
      <RightArrowIcon className="h-[41.6px] w-[41.6px] text-backgroundWhite fill-current" />
    </button>
  )
}

type ButtonInfo = {
  label?: string,
  buttonIcon?: IconType,
  iconClassName?: string
}
interface CustomButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  buttonInfo: ButtonInfo
}

export function PrimaryButton({buttonInfo, className, disabled, ...props}: CustomButtonProps) {
  return (
    <button
      className={classNames("flex px-4 py-3 rounded-[12px] text-style-body2-bold text-backgroundWhite justify-center items-center",
        disabled ? "bg-gray40" : "bg-primaryAction primary-button-drop-shadow hover:bg-primaryActionBtnHover primary-button-hover-shadow",
        className)}
      disabled={disabled}
      {...props}
    >
      {disabled ?
        <>
          <PulseLoader color='white'/>
        </> :
        <>
          {buttonInfo.buttonIcon && <buttonInfo.buttonIcon className={classNames(buttonInfo.label ? "mr-2" : "", "h-6 w-6 fill-current",
            buttonInfo.iconClassName)} />}
          {buttonInfo.label}
        </>
      }
    </button>
  )
}

export function SecondaryButton({buttonInfo, className, disabled, ...props}: CustomButtonProps) {
  return (
    <button
      className={classNames("flex px-4 py-3 rounded-[12px] text-style-body2-bold justify-center items-center border",
        disabled ? "border-gray10 text-gray50" : "border-primaryAction text-primaryAction secondary-button-drop-shadow hover:border-primaryActionBtnHover hover:text-primaryActionBtnHover secondary-button-hover-shadow",
        className)}
      disabled={disabled}
      {...props}
    >
      {buttonInfo.buttonIcon && <buttonInfo.buttonIcon className={classNames(buttonInfo.label ? "mr-2" : "", "h-6 w-6 fill-current", 
        buttonInfo.iconClassName)} />}
      {buttonInfo.label}
    </button>
  )
}

export function SecondaryRedButton({buttonInfo, className, disabled, ...props}: CustomButtonProps) {
  return (
    <button
      className={classNames("flex px-4 py-3 rounded-[12px] text-style-body2-bold justify-center items-center bg-backgroundWhite border",
        disabled ? "border-gray10 text-gray50" : "border-tertiaryRed text-tertiaryRed cancel-button-drop-shadow hover:border-tertiaryRedDarker hover:text-tertiaryRedDarker cancel-button-hover-shadow",
        className)}
      disabled={disabled}
      {...props}
    >
      {buttonInfo.buttonIcon && <buttonInfo.buttonIcon className={classNames(buttonInfo.label ? "mr-2" : "", "h-6 w-6 fill-current", 
        buttonInfo.iconClassName)} />}
      {buttonInfo.label}
    </button>
  )
}

export function CancelButton({buttonInfo, className, disabled, ...props}: CustomButtonProps) {
  return (
    <button
      className={classNames("flex px-4 py-3 rounded-[12px] text-style-body2-bold text-backgroundWhite justify-center items-center",
        disabled ? "bg-gray40" : "bg-tertiaryRed cancel-button-drop-shadow hover:bg-tertiaryRedDarker cancel-button-hover-shadow",
        className)}
      disabled={disabled}
      {...props}
    >
      {disabled ?
        <>
          <PulseLoader color='white'/>
        </> :
        <>
          {buttonInfo.buttonIcon && <buttonInfo.buttonIcon className={classNames(buttonInfo.label ? "mr-2" : "", "h-6 w-6 fill-current",
            buttonInfo.iconClassName)} />}
          {buttonInfo.label}
        </>
      }
    </button>
  )
}

export function TertiaryButton({buttonInfo, className, disabled, ...props}: CustomButtonProps) {

  return (
    <button
      className={classNames("flex px-4 py-3 rounded-[12px] text-style-body2-bold justify-center items-center",
        disabled ? "text-gray50" : "text-primaryAction hover:text-primaryActionBtnHover",
        className)}
      disabled={disabled}
      {...props}
    >
      {buttonInfo.buttonIcon && <buttonInfo.buttonIcon className={classNames(buttonInfo.label ? "mr-2" : "", "h-6 w-6 fill-current", 
        buttonInfo.iconClassName)} />}
      {buttonInfo.label}
    </button>
  )
}

export function TertiaryGrayButton({buttonInfo, className, disabled, ...props}: CustomButtonProps) {

  return (
    <button
      className={classNames("flex px-4 py-3 rounded-[12px] text-style-body2-bold justify-center items-center",
        disabled ? "text-gray50" : "text-gray150 hover:text-tertiaryRedDarker",
        className)}
      disabled={disabled}
      {...props}
    >
      {buttonInfo.buttonIcon && <buttonInfo.buttonIcon className={classNames(buttonInfo.label ? "mr-2" : "", "h-6 w-6 fill-current", 
        buttonInfo.iconClassName)} />}
      {buttonInfo.label}
    </button>
  )
}